import React from 'react';
import { Article, CurrencyBtc, Wallet, Lightning } from '@phosphor-icons/react';
import { motion } from 'framer-motion';

const CryptoGuide = () => {
  return (
    <div className="min-h-screen py-16" data-testid="crypto-guide-page">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <Article size={64} weight="duotone" className="neon-cyan mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-black mb-4 tracking-tighter">Crypto Guide</h1>
          <p className="text-xl text-gray-400">Your primer on cryptocurrency and the NextXus token economy</p>
        </div>

        {/* Hero Image */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12 rounded-sm overflow-hidden"
        >
          <img
            src="https://images.pexels.com/photos/8370773/pexels-photo-8370773.jpeg"
            alt="Cryptocurrency"
            className="w-full h-64 object-cover"
          />
        </motion.div>

        {/* Content Sections */}
        <div className="space-y-12">
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="p-8 glass-surface border border-white/10 rounded-sm"
            data-testid="section-what-is-crypto"
          >
            <div className="flex items-center gap-3 mb-4">
              <CurrencyBtc size={32} className="neon-cyan" />
              <h2 className="text-2xl font-bold">What is Cryptocurrency?</h2>
            </div>
            <p className="text-gray-300 leading-relaxed mb-4">
              Cryptocurrency is digital money that operates on blockchain technology—a decentralized network that records transactions across many computers. Unlike traditional currencies controlled by governments, crypto is peer-to-peer, borderless, and transparent.
            </p>
            <p className="text-gray-300 leading-relaxed">
              Bitcoin (BTC) was the first, launched in 2009. Since then, thousands of cryptocurrencies have emerged, each with unique features: Ethereum (ETH) for smart contracts, Solana (SOL) for speed, and more.
            </p>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-8 glass-surface border border-white/10 rounded-sm"
            data-testid="section-why-crypto"
          >
            <div className="flex items-center gap-3 mb-4">
              <Lightning size={32} className="neon-amber" />
              <h2 className="text-2xl font-bold">Why Use Crypto for NextXus Tokens?</h2>
            </div>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start gap-3">
                <span className="text-[#00F0FF] text-xl">•</span>
                <span><strong className="text-white">Speed:</strong> Transactions settle in minutes, not days.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#00F0FF] text-xl">•</span>
                <span><strong className="text-white">Global Access:</strong> No banks, no borders. Anyone, anywhere can participate.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#00F0FF] text-xl">•</span>
                <span><strong className="text-white">Transparency:</strong> Every transaction is recorded on the blockchain.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#00F0FF] text-xl">•</span>
                <span><strong className="text-white">Security:</strong> Cryptographic encryption keeps your funds safe.</span>
              </li>
            </ul>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="p-8 glass-surface border border-white/10 rounded-sm"
            data-testid="section-how-to-pay"
          >
            <div className="flex items-center gap-3 mb-4">
              <Wallet size={32} className="neon-green" />
              <h2 className="text-2xl font-bold">How to Pay with Crypto</h2>
            </div>
            <div className="space-y-4 text-gray-300">
              <div>
                <h3 className="font-bold text-white mb-2">Step 1: Get a Wallet</h3>
                <p>Download a crypto wallet app like Coinbase Wallet, MetaMask, Phantom, or Trust Wallet. These apps let you store and send cryptocurrency.</p>
              </div>
              <div>
                <h3 className="font-bold text-white mb-2">Step 2: Buy Crypto</h3>
                <p>Purchase cryptocurrency through an exchange like Coinbase, Binance, or Kraken using your bank account or credit card.</p>
              </div>
              <div>
                <h3 className="font-bold text-white mb-2">Step 3: Send Payment</h3>
                <p>On our Token Checkout page, select your preferred cryptocurrency (BTC, ETH, SOL, etc.), copy the wallet address, and send the exact amount from your wallet.</p>
              </div>
              <div>
                <h3 className="font-bold text-white mb-2">Step 4: Submit Proof</h3>
                <p>After sending, copy your transaction ID (hash) and submit it through our form. We'll verify and credit your tokens within 24 hours.</p>
              </div>
            </div>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="p-8 bg-gradient-to-r from-[#00F0FF]/10 to-[#FFB800]/10 border border-[#00F0FF]/30 rounded-sm"
            data-testid="section-token-economy"
          >
            <h2 className="text-2xl font-bold mb-4 neon-cyan">NextXus Token Economy</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Our token system is simple: <strong className="text-white">$5 = 1 Token</strong>. Tokens are the universal currency of the NextXus ecosystem.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              Use tokens to redeem exclusive merch, access premium features, and participate in the Federation network. Your tokens are tied to your unique Federation ID and never expire.
            </p>
            <div className="inline-block px-6 py-3 bg-[#00F0FF] text-[#030305] font-bold rounded-sm mt-4">
              <a href="/tokens" data-testid="get-tokens-link">Get Tokens Now →</a>
            </div>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="p-8 glass-surface border border-white/10 rounded-sm"
            data-testid="section-faq"
          >
            <h2 className="text-2xl font-bold mb-6">Quick FAQs</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-bold text-white mb-2">Is crypto safe?</h3>
                <p className="text-gray-300 text-sm">Yes, when used correctly. Always double-check wallet addresses before sending, and only use reputable wallets and exchanges.</p>
              </div>
              <div>
                <h3 className="font-bold text-white mb-2">Can I use PayPal instead?</h3>
                <p className="text-gray-300 text-sm">Absolutely! We accept PayPal Direct Deposit. Just scan the QR code for your chosen pack on the Token Checkout page.</p>
              </div>
              <div>
                <h3 className="font-bold text-white mb-2">How long does verification take?</h3>
                <p className="text-gray-300 text-sm">Most purchases are verified within 24 hours. You'll see your token balance update once confirmed.</p>
              </div>
              <div>
                <h3 className="font-bold text-white mb-2">What if I send the wrong amount?</h3>
                <p className="text-gray-300 text-sm">Contact our support team with your transaction details. We'll help sort it out.</p>
              </div>
            </div>
          </motion.section>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center mt-16"
        >
          <p className="text-gray-400 mb-4">Ready to join the NextXus ecosystem?</p>
          <a
            href="/tokens"
            className="inline-block px-10 py-4 bg-[#00F0FF] text-[#030305] font-bold rounded-sm hover:bg-[#00D9E8] transition-colors"
            data-testid="cta-get-tokens"
          >
            Get Your Tokens
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default CryptoGuide;